/**
 * Reusable traveller autocomplete component.
 * Provides search and autofill functionality for traveller fields.
 */

import { fetchJson, buildQueryString } from './api-client';
import { escapeHtml, setFieldValue, formatDateForInput } from './helpers';
import type { TravellerResult } from './types';

export interface AutocompleteOptions {
    searchUrl: string;
    agencyIdGetter: () => string;
    onSelect: (traveller: TravellerResult) => void;
    minQueryLength?: number;
    debounceMs?: number;
}

/**
 * Setup autocomplete for a traveller search input.
 * Returns a teardown function — call it when the input is removed from the DOM
 * to prevent the outside-click listener from leaking.
 */
export function setupTravellerAutocomplete(
    searchInput: HTMLInputElement,
    resultsContainer: HTMLElement,
    options: AutocompleteOptions
): () => void {
    const { searchUrl, agencyIdGetter, onSelect, minQueryLength = 2, debounceMs = 300 } = options;

    // Per-instance timer so multiple rows don't cancel each other's searches
    let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    searchInput.addEventListener('input', function () {
        const query = (this as HTMLInputElement).value.trim();
        const agencyId = agencyIdGetter();

        if (searchDebounceTimer) clearTimeout(searchDebounceTimer);

        if (query.length < minQueryLength || !agencyId) {
            resultsContainer.classList.add('d-none');
            resultsContainer.innerHTML = '';
            return;
        }

        searchDebounceTimer = setTimeout(async () => {
            try {
                const queryStr = buildQueryString({ agencyId, query });
                const data = await fetchJson<TravellerResult[]>(`${searchUrl}?${queryStr}`);

                if (!data || data.length === 0) {
                    resultsContainer.innerHTML = '<div class="list-group-item text-muted small">No matching travellers found.</div>';
                    resultsContainer.classList.remove('d-none');
                    return;
                }

                renderResults(data, resultsContainer, onSelect, searchInput);
            } catch (error) {
                console.error('Traveller autocomplete error:', error);
                resultsContainer.classList.add('d-none');
            }
        }, debounceMs);
    });

    // One listener per instance — returned teardown removes it
    const outsideClick = (e: MouseEvent) => {
        if (!searchInput.contains(e.target as Node) && !resultsContainer.contains(e.target as Node)) {
            resultsContainer.classList.add('d-none');
        }
    };
    document.addEventListener('click', outsideClick);

    return () => document.removeEventListener('click', outsideClick);
}

function renderResults(
    travellers: TravellerResult[],
    container: HTMLElement,
    onSelect: (t: TravellerResult) => void,
    searchInput: HTMLInputElement
) {
    let html = '';
    travellers.forEach((t) => {
        html += `<button type="button" class="list-group-item list-group-item-action small traveller-result-item" data-traveller='${escapeHtml(JSON.stringify(t))}'>`;
        html += `<strong>${escapeHtml(t.firstName || '')} ${escapeHtml(t.lastName || '')}</strong>`;
        if (t.email) html += ` <span class="text-muted">— ${escapeHtml(t.email)}</span>`;
        if (t.phone) html += ` <span class="text-muted ms-1">(${escapeHtml(t.phone)})</span>`;
        html += '</button>';
    });

    container.innerHTML = html;
    container.classList.remove('d-none');

    container.querySelectorAll<HTMLElement>('.traveller-result-item').forEach((btn) => {
        btn.addEventListener('click', () => {
            const traveller: TravellerResult = JSON.parse(btn.getAttribute('data-traveller') || '{}');
            onSelect(traveller);
            container.classList.add('d-none');
            container.innerHTML = '';
            searchInput.value = '';
        });
    });
}

/**
 * Fill traveller form fields from a TravellerResult object
 */
export function fillTravellerFields(
    traveller: TravellerResult,
    travellerPrefix: string,
    contactPrefix: string,
    foreignSectionId?: string
) {
    // Personal details
    setFieldValue(`${travellerPrefix}.FirstName`, traveller.firstName || '');
    setFieldValue(`${travellerPrefix}.LastName`, traveller.lastName || '');
    setFieldValue(`${travellerPrefix}.DateOfBirth`, traveller.dateOfBirth ? formatDateForInput(traveller.dateOfBirth) : '');
    setFieldValue(`${travellerPrefix}.Gender`, traveller.gender != null ? String(traveller.gender) : '');
    setFieldValue(`${travellerPrefix}.JMBG`, traveller.jmbg || '');
    setFieldValue(`${travellerPrefix}.IsForeignCitizen`, traveller.isForeignCitizen != null ? String(traveller.isForeignCitizen) : '');

    // Contact details
    setFieldValue(`${contactPrefix}.Email`, traveller.email || '');
    setFieldValue(`${contactPrefix}.Phone`, traveller.phone || '');
    setFieldValue(`${contactPrefix}.Address`, traveller.address || '');
    setFieldValue(`${contactPrefix}.City`, traveller.city || '');
    setFieldValue(`${contactPrefix}.Municipality`, traveller.municipality || '');
    setFieldValue(`${contactPrefix}.Zip`, traveller.zip || '');
    setFieldValue(`${contactPrefix}.Country`, traveller.country || '');

    // Identity documents
    setFieldValue(`${contactPrefix}.IdNumber`, traveller.idNumber || '');
    setFieldValue(`${contactPrefix}.PassportNumber`, traveller.passportNumber || '');
    setFieldValue(`${contactPrefix}.PassportExpirationDate`, traveller.passportExpirationDate ? formatDateForInput(traveller.passportExpirationDate) : '');

    // Sync hidden FullName and Birthday
    setFieldValue(`${contactPrefix}.FullName`, `${traveller.firstName || ''} ${traveller.lastName || ''}`.trim());
    setFieldValue(`${contactPrefix}.Birthday`, traveller.dateOfBirth ? formatDateForInput(traveller.dateOfBirth) : '');

    // Nationality & Residence
    setFieldValue(`${travellerPrefix}.CitizenshipCountryId`, traveller.citizenshipCountryId != null ? String(traveller.citizenshipCountryId) : '');
    setFieldValue(`${travellerPrefix}.BirthCountryId`, traveller.birthCountryId != null ? String(traveller.birthCountryId) : '');
    setFieldValue(`${travellerPrefix}.ResidenceCountryId`, traveller.residenceCountryId != null ? String(traveller.residenceCountryId) : '');
    setFieldValue(`${travellerPrefix}.ResidencePlace`, traveller.residencePlace || '');
    setFieldValue(`${travellerPrefix}.ResidenceAddress`, traveller.residenceAddress || '');

    // Foreign ID document
    setFieldValue(`${travellerPrefix}.ForeignIdDocumentType`, traveller.foreignIdDocumentType != null ? String(traveller.foreignIdDocumentType) : '');
    setFieldValue(`${travellerPrefix}.ForeignIdDocumentNumber`, traveller.foreignIdDocumentNumber || '');
    setFieldValue(`${travellerPrefix}.ForeignIdDocumentIssuedDate`, traveller.foreignIdDocumentIssuedDate ? formatDateForInput(traveller.foreignIdDocumentIssuedDate) : '');
    setFieldValue(`${travellerPrefix}.ForeignIdDocumentIssuedBy`, traveller.foreignIdDocumentIssuedBy || '');

    // Visa
    setFieldValue(`${travellerPrefix}.VisaType`, traveller.visaType != null ? String(traveller.visaType) : '');
    setFieldValue(`${travellerPrefix}.VisaNumber`, traveller.visaNumber || '');
    setFieldValue(`${travellerPrefix}.VisaIssuedPlace`, traveller.visaIssuedPlace || '');

    // Toggle foreign document section visibility
    if (foreignSectionId) {
        const foreignSection = document.getElementById(foreignSectionId);
        if (foreignSection) {
            foreignSection.style.display = traveller.isForeignCitizen === true ? '' : 'none';
        }
    }
}
